import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Send, Loader2, CornerDownRight, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { timeAgo } from "@/lib/format";

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
  "bg-amber-100 text-amber-700",
  "bg-purple-100 text-purple-700",
  "bg-red-100 text-red-700",
  "bg-teal-100 text-teal-700",
];

function avatarColor(name) {
  const sum = (name || "?").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function CommentItem({ c, onReply, isReply }) {
  return (
    <div className={`flex gap-3 ${isReply ? "mt-4" : ""}`} data-testid={`comment-${c.id}`}>
      <div className={`flex ${isReply ? "h-8 w-8 text-xs" : "h-10 w-10 text-sm"} shrink-0 items-center justify-center rounded-full font-display font-bold ${avatarColor(c.name)}`}>
        {(c.name || "?").charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-sm font-bold text-slate-900">{c.name}</span>
          {c.reply_to_name && (
            <span className="inline-flex items-center gap-0.5 text-xs text-slate-400">
              <CornerDownRight className="h-3 w-3" />
              {c.reply_to_name}
            </span>
          )}
          <span className="text-xs text-slate-400">{timeAgo(c.created_at)}</span>
        </div>
        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{c.content}</p>
        <button
          onClick={() => onReply(c)}
          data-testid={`comment-reply-btn-${c.id}`}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          <CornerDownRight className="h-3.5 w-3.5" />
          Отговори
        </button>
      </div>
    </div>
  );
}

export default function CommentsSection({ slug }) {
  const settings = useSettings();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const { data: comments, isLoading } = useQuery({
    queryKey: ["comments", slug],
    queryFn: () => api.get(`/articles/${slug}/comments`).then((r) => r.data),
  });

  const list = comments || [];

  const tree = useMemo(() => {
    const ids = new Set(list.map((c) => c.id));
    const repliesByParent = {};
    const roots = [];
    for (const c of list) {
      if (c.parent_id && ids.has(c.parent_id)) {
        (repliesByParent[c.parent_id] = repliesByParent[c.parent_id] || []).push(c);
      } else {
        roots.push(c);
      }
    }
    return { roots, repliesByParent };
  }, [list]);

  if (settings.comments_enabled === false) return null;

  const startReply = (c) => {
    setReplyTo({ id: c.id, name: c.name });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !content.trim()) {
      toast.error("Моля попълнете име и коментар.");
      return;
    }
    setLoading(true);
    try {
      const payload = { name: name.trim(), content: content.trim() };
      if (replyTo) payload.parent_id = replyTo.id;
      const { data } = await api.post(`/articles/${slug}/comments`, payload);
      if (data.status === "approved") {
        qc.invalidateQueries({ queryKey: ["comments", slug] });
        qc.invalidateQueries({ queryKey: ["article", slug] });
        toast.success("Коментарът е публикуван!");
      } else {
        toast.success("Благодарим! Коментарът ще се появи след одобрение от редакцията.");
      }
      setContent("");
      setReplyTo(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20";

  return (
    <section className="mt-14 border-t border-slate-200 pt-10" data-testid="comments-section">
      <div className="mb-6 flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-primary" />
        <h2 className="font-display text-2xl font-extrabold text-slate-900">
          Коментари {list.length > 0 && <span className="text-slate-400">({list.length})</span>}
        </h2>
      </div>

      <form ref={formRef} onSubmit={submit} className="mb-8 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="comment-form">
        {replyTo && (
          <div className="mb-3 flex items-center justify-between rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary" data-testid="reply-indicator">
            <span className="inline-flex items-center gap-1.5">
              <CornerDownRight className="h-4 w-4" />
              Отговаряте на <b>{replyTo.name}</b>
            </span>
            <button type="button" onClick={() => setReplyTo(null)} className="rounded p-1 hover:bg-primary/10" aria-label="Отказ">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="mb-3">
          <input
            className={inputClass}
            data-testid="comment-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Вашето име"
            maxLength={80}
          />
        </div>
        <textarea
          rows={3}
          className={inputClass}
          data-testid="comment-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={replyTo ? `Вашият отговор до ${replyTo.name}...` : "Споделете мнение по темата... (бъдете любезни)"}
          maxLength={2000}
        />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            {settings.comments_require_approval
              ? "Коментарите се публикуват след одобрение."
              : "Коментарите се публикуват веднага."}
          </span>
          <button
            type="submit"
            disabled={loading}
            data-testid="comment-submit-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0041A8] disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {replyTo ? "Отговори" : "Публикувай"}
          </button>
        </div>
      </form>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : list.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500" data-testid="no-comments">
          Все още няма коментари. Бъдете първият, който ще сподели мнение!
        </p>
      ) : (
        <div className="space-y-6" data-testid="comments-list">
          {tree.roots.map((c) => (
            <div key={c.id}>
              <CommentItem c={c} onReply={startReply} />
              {(tree.repliesByParent[c.id] || []).length > 0 && (
                <div className="ml-6 mt-2 border-l-2 border-slate-100 pl-4">
                  {tree.repliesByParent[c.id].map((r) => (
                    <CommentItem key={r.id} c={r} onReply={startReply} isReply />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
